// Order-related enums and types

// ─── Three-Axis Order State ─────────────────────────────────────────────────

/**
 * Order State - tracks the BUSINESS LIFECYCLE of an order
 *
 * This is one of two independent axes on the Order document:
 *   orderState   → business lifecycle (this)
 *   kitchenState → production/fulfillment
 *
 * Payment state lives on Check documents (checkState + check paymentStatus).
 * Transitions are validated by the order state machine.
 * `closed` is terminal and set automatically by the composite close check
 * when kitchenState is complete AND all checks are settled.
 */
export type OrderState =
    | 'draft'              // Order is being built (not committed)
    | 'pendingAcceptance'  // External order awaiting acceptance
    | 'scheduled'          // Accepted but scheduled for future time
    | 'open'               // Active order (items can be added, sent to kitchen, paid)
    | 'held'               // Temporarily paused (manager action)
    | 'closed'             // Terminal: fully served AND paid (set by composite check only)
    | 'cancelled'          // Terminal: soft cancel (no food committed to kitchen)
    | 'voided'             // Terminal: hard void (food cost incurred, requires manager + reason)
    | 'transferred'        // Terminal: all items transferred to another order (audit trail)
    // ── Checkout-held (docs/superpowers/specs/2026-09-30-online-order-persist-first-design.md) ──
    // An online order persisted BEFORE its card is charged, exactly as the POS
    // persists an order before it takes payment. INVISIBLE and INERT to every
    // consumer (see PRE_PAYMENT_ORDER_STATES): no board, no kitchen, no print,
    // no Books, no report. Leaves ONLY through the checkout completer
    // (validateCheckoutOrderTransition), never the general state machine.
    | 'awaitingPayment'    // Persisted with its check and real ids; the charge has not been approved
    | 'abandoned';         // The checkout never paid. Holds no number; revived only by a new attempt on the same cart

/**
 * Kitchen State - tracks PRODUCTION/FULFILLMENT progress at the order level
 *
 * Derived from the aggregate of per-item `kitchenStatus` values.
 * For retail/non-kitchen orders, this is `none`.
 */
export type KitchenState =
    | 'none'               // No kitchen involvement (retail, non-kitchen orders)
    | 'pending'            // Items added but not sent to kitchen
    | 'sent'               // Items sent to kitchen, not yet started
    | 'preparing'          // Kitchen actively preparing items
    | 'ready'              // All items ready for service
    | 'served';            // All items served to customer

/**
 * Payment Status - tracks the FINANCIAL state of a Check or PaymentRecord
 *
 * Lives on Check documents, NOT on Order documents.
 * Order-level payment status is derived from checks via deriveOrderPaymentStatus().
 * Enhanced to distinguish void vs refund and partial refunds.
 */
export type PaymentStatus =
    | 'unpaid'             // No payment received yet
    | 'processing'         // Payment is being processed right now
    | 'partiallyPaid'      // Partial payment received (split bills, deposits)
    | 'paid'               // Full payment received and cleared
    | 'partiallyRefunded'  // Some money returned post-settlement, rest retained
    | 'refunded'           // Full payment refunded post-settlement
    | 'voided'             // Payment reversed pre-settlement (no interchange fees)
    | 'failed'             // Payment attempt failed (declined card, etc.)
    | 'comped';            // Order was comped (no payment required)

export type OrderType = 'dineIn' | 'takeOut' | 'delivery' | 'quickService';

// Kitchen workflow enums (per-item status)
/**
 * Kitchen Status - per-item kitchen workflow state
 *
 * `voided` was added to distinguish items removed after kitchen commitment
 * (food cost tracked) from items cancelled before kitchen (`cancelled`).
 */
export type KitchenStatus = 'pending' | 'sentToKitchen' | 'inPreparation' | 'ready' | 'served' | 'fired' | 'cancelled' | 'voided';
export type KitchenSendPriority = 'low' | 'normal' | 'high' | 'urgent';

/** Every value of {@link KitchenSendPriority}, in rising urgency. */
export const KITCHEN_SEND_PRIORITIES: readonly KitchenSendPriority[] = [
  'low',
  'normal',
  'high',
  'urgent',
];

/**
 * Whether a kitchen priority makes a ticket RUSH — the banner on the chit and
 * the jump to the front of its printer's queue.
 *
 * The ONE rule, shared by the engine and the POS (which mirrors it and is
 * pinned against it by `packages/print-conformance/fixtures/kitchen-routing.json`).
 */
export function isRushKitchenPriority(priority: KitchenSendPriority): boolean {
  return priority === 'high' || priority === 'urgent';
}

/**
 * Read a stored kitchen priority, whatever wrote it.
 *
 * Two vocabularies reached this one field. This union is URGENCY; the POS's
 * enum was `immediate | normal | delayed | manual`, which is FIRE TIMING — and
 * it wrote those names straight to Firestore. Nothing here recognised them, so
 * a rush line rung on a till was never rush on the server's chit. The POS now
 * writes this vocabulary; this is what keeps the documents already written
 * readable:
 *
 *   - `immediate` → `high`. It was the only value any code acted on, and it
 *     acted on it as rush.
 *   - `delayed`, `manual` → `normal`. Nothing ever read them. Timing is owned
 *     by coursing (fire / hold), which superseded them.
 *   - `rush` → `high`. The request-level string older tills send.
 *
 * Anything unrecognised is `normal`, never an error: an unknown priority must
 * not stop food reaching a kitchen.
 */
export function normalizeKitchenPriority(value: unknown): KitchenSendPriority {
  switch (value) {
    case 'low':
    case 'normal':
    case 'high':
    case 'urgent':
      return value;
    case 'immediate':
    case 'rush':
      return 'high';
    default:
      return 'normal';
  }
}

// Sync states for offline-first operations
export type OrderSyncState =
    | 'localOnly'       // Only exists locally
    | 'pendingSync'     // Needs to be synced
    | 'syncing'         // Currently syncing
    | 'synced'          // Successfully synced
    | 'syncFailed'      // Sync failed
    | 'conflicted';     // Sync conflict

export type OrderSyncPriority = 'low' | 'normal' | 'high' | 'critical';

// Order source types - where the order originated from
/**
 * Where an order was TAKEN.
 *
 * `api` means an integration posted it — a real, specific claim. It is not a
 * fallback, and nothing may use it as one: the order-details report did, so
 * every in-house ticket at House of Pho read "API" as its source (measured
 * 2026-09-11) while nothing in this codebase has ever written `'api'` onto an
 * order. `other` is the honest bucket for a source we cannot name — it is what
 * the financial warehouse's own `order_source` enum already calls it.
 */
export type OrderSource =
    | 'pos'
    | 'online'
    | 'thirdParty'
    | 'kiosk'
    | 'phone'
    | 'api'
    | 'other';

// External order source - specific third-party platform
/** External order source platforms. 'internal' indicates POS/native app orders */
/**
 * Where an external order ORIGINATED — attribution, not transport.
 *
 * For a direct integration the marketplace and the adapter are the same name.
 * For an AGGREGATOR they are not: a KitchenHub-brokered Uber Eats order is
 * `'ubereats'` here, never `'kitchenhub'`, so that moving that marketplace onto
 * a direct integration later is a routing change and historical orders keep
 * their attribution (docs/KITCHENHUB_API.md D1).
 *
 * `'kitchenhub'` IS a legal value, but only as the honest fallback for an order
 * brokered by KitchenHub from a marketplace we cannot map — an aggregator's
 * catalog grows without notice, and recording "via KitchenHub, marketplace
 * unrecognized" is better than either guessing a marketplace or refusing a real
 * customer's order. Use `deliveryPlatformFromProviderId()` first; fall back to
 * `'kitchenhub'` only when it returns undefined.
 */
export type ExternalOrderSource =
    | 'ubereats'
    | 'doordash'
    | 'grubhub'
    | 'chownow'
    | 'foodhub'
    | 'gloriafood'
    | 'flipdish'
    | 'beyondmenu'
    | 'cloudwaitress'
    | 'eatstreet'
    | 'menufy'
    | 'kitchenhub'
    | 'square'
    | 'toast'
    | 'internal';

// ─── Check State (Axis 3) ───────────────────────────────────────────────────

/**
 * Check State - tracks the FINANCIAL ALLOCATION LIFECYCLE of a check
 *
 * This is the third axis in the 5-axis order state model:
 *   Axis 1: orderState    → business lifecycle (Order doc)
 *   Axis 2: kitchenState  → production/fulfillment (Order.items[] + RTDB)
 *   Axis 3: checkState    → financial allocation lifecycle (Check doc) ← THIS
 *   Axis 4: paymentStatus → per-check payment aggregate (Check doc, derived from Axis 5)
 *   Axis 5: transaction   → per-PaymentRecord processor state (PaymentRecord doc)
 *
 * Checks are first-class Firestore documents in the `checks` collection.
 * `finalized` locks the check for payment — no item changes allowed.
 */
export type CheckState =
    | 'open'              // Items can be added/removed/reassigned between checks
    | 'finalized'         // Payment in progress — items locked, no modifications
    | 'closed'            // Terminal: fully paid and settled
    | 'voided'            // Terminal: check voided (manager action, items reassigned or order voided)
    | 'superseded';       // Terminal: check replaced by a split operation (routine, audit trail only)

/**
 * Why a CLOSED check or order went back to `open` — the reopen stamp
 * (`reopenedAt`, `reopenReason`, `reopenedByPaymentGuid`) written on both.
 *
 * `payment_voided`: a payment was voided after the ticket closed, so its amount
 * is owed again (docs/PAYMENT_REVERSAL_ARCHITECTURE.md §Reopen on void). Written
 * by the payment cascade; a manager reopen writes no reason.
 */
export type ReopenReason = 'payment_voided';

// Check splitting
/** Split status of the order's checks */
export type OrderSplitStatus = 'unsplit' | 'split' | 'partiallySplit' | 'merged';

// Discount and tax types

/**
 * How a discount in the CATALOG is expressed — a management-configured
 * `Discount` document (`models/discounts/Discount.ts`, `models/menus/Discount.ts`).
 * Deliberately narrow: `comp` is never configured, only applied, so widening
 * this would let a catalog row claim to be a comp and the `/discounts` routes
 * would still refuse it at the wire.
 */
export type DiscountType = 'percentage' | 'fixedAmount';

/**
 * How a discount ALREADY ON an order or a line is expressed.
 *
 * `comp` is the third one, and it is a different QUESTION from a discount:
 * a discount is a price the guest agreed to, a comp is food the house gave
 * away — what shrink, food cost and manager accountability are read from.
 * The POS has always written it (`DiscountType.comp` in
 * `apps/pos/lib/constants/enums.dart`), `ICheckDiscount.discountType` has
 * always carried it, and `SalesReport.compedAmount` has always reported it.
 *
 * 🔴 Only the APPLIED union knew about it, so `AppliedDiscount.fromJSON`
 * threw `invalid discount type` on a comped line. That parse runs inside
 * `OrderItem.fromJSON` → `Order.fromJSON` → `snapshot.docs.map(...)`, so ONE
 * comped line made the whole orders read fail server-side — the order was
 * unreadable, not just mis-totalled.
 *
 * **Money semantics.** A comp is a 100%-of-line giveaway whose authority is
 * `amountApplied`; `value` is 100 and is a DISPLAY hint ("100%"), never a
 * second source of truth. Nothing may re-derive a comp's money from `value`
 * as a percentage — every money path reads `amountApplied`, exactly as it
 * already does for `fixedAmount`.
 */
export type AppliedDiscountType = DiscountType | 'comp';

/** The applied-discount types, for runtime validation. Order is not meaningful. */
export const APPLIED_DISCOUNT_TYPES: readonly AppliedDiscountType[] = Object.freeze([
    'percentage',
    'fixedAmount',
    'comp',
] as const);

/** Whether `value` is an applied-discount type this build understands. */
export function isAppliedDiscountType(value: unknown): value is AppliedDiscountType {
    return typeof value === 'string' && (APPLIED_DISCOUNT_TYPES as readonly string[]).includes(value);
}

export type DiscountScope = 'order' | 'item';
export type TaxType = 'percentage' | 'fixed' | 'none';
